/**
 * Service Organizations — READ-ONLY
 * Les modifications se font depuis Supabase directement ou le LMS
 */
import { supabase } from '../config/supabase';
import { Organization, OrganizationWithStats, OrganizationStatus } from '../types';

const toOrganization = (row: Record<string, unknown>): Organization => ({
  id: row.id as string,
  name: row.name as string,
  slug: row.slug as string | undefined,
  brandColor: row.brand_color as string | undefined,
  logoUrl: row.logo_url as string | undefined,
  maxLearners: (row.max_learners as number | null) ?? null,
  status: (row.status as OrganizationStatus) || 'active',
  contactName: row.contact_name as string | undefined,
  contactEmail: row.contact_email as string | undefined,
  contactPhone: row.contact_phone as string | undefined,
  address: row.address as string | undefined,
  notes: row.notes as string | undefined,
  createdAt: new Date(row.created_at as string),
  updatedAt: new Date(row.updated_at as string),
});

export const organizationsService = {
  async getAll(): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching organizations:', error);
      throw new Error('Impossible de charger les organisations');
    }

    return (data || []).map(toOrganization);
  },

  /**
   * Get organizations with learner count + completion rate.
   * Uses parallel queries: orgs, learners (count), and RPC for completion stats.
   */
  async getAllWithStats(): Promise<OrganizationWithStats[]> {
    // Fetch orgs and all learners in parallel
    const [orgsResult, learnersResult] = await Promise.all([
      supabase.from('organizations').select('*').order('name'),
      supabase.from('learners').select('organization_id, id'),
    ]);

    if (orgsResult.error) {
      console.error('Error fetching organizations:', orgsResult.error);
      throw new Error('Impossible de charger les organisations');
    }

    // Build learner count per org
    const learnerCountMap = new Map<string, number>();
    (learnersResult.data || []).forEach((l) => {
      const orgId = l.organization_id as string;
      learnerCountMap.set(orgId, (learnerCountMap.get(orgId) || 0) + 1);
    });

    // Fetch completion stats via RPC (SECURITY DEFINER — reliable)
    // Use null org_id to get stats across all orgs
    const { data: _statusData } = await supabase.rpc('get_enrollment_status_distribution', {
      p_org_id: null,
    });

    // The RPC returns global stats, not per-org. For per-org completion,
    // we fetch all enrollments + course_progress and compute in JS.
    const [enrollmentsResult, progressResult] = await Promise.all([
      supabase.from('enrollments').select('id, learner_id, course_id'),
      supabase.from('course_progress').select('enrollment_id, completed_at'),
    ]);

    // Build enrollment → completed map
    const completedEnrollmentIds = new Set<string>();
    (progressResult.data || []).forEach((cp) => {
      if (cp.completed_at) {
        completedEnrollmentIds.add(cp.enrollment_id);
      }
    });

    // Build per-learner enrollment stats
    const learnerOrgMap = new Map<string, string>();
    (learnersResult.data || []).forEach((l) => {
      learnerOrgMap.set(l.id as string, l.organization_id as string);
    });

    // Count enrollments and completions per org
    const orgEnrollmentStats = new Map<string, { total: number; completed: number }>();
    (enrollmentsResult.data || []).forEach((e) => {
      const orgId = learnerOrgMap.get(e.learner_id);
      if (!orgId) return;
      if (!orgEnrollmentStats.has(orgId)) {
        orgEnrollmentStats.set(orgId, { total: 0, completed: 0 });
      }
      const s = orgEnrollmentStats.get(orgId)!;
      s.total++;
      if (completedEnrollmentIds.has(e.id)) {
        s.completed++;
      }
    });

    const orgs = (orgsResult.data || []).map(toOrganization);

    return orgs.map((org) => {
      const enrollStats = orgEnrollmentStats.get(org.id) || { total: 0, completed: 0 };
      return {
        ...org,
        learnersCount: learnerCountMap.get(org.id) || 0,
        completedCount: enrollStats.completed,
        completionRate: enrollStats.total > 0
          ? Math.round((enrollStats.completed / enrollStats.total) * 100)
          : 0,
      };
    });
  },

  async getById(id: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching organization:', error);
      throw new Error("Impossible de charger l'organisation");
    }

    return data ? toOrganization(data) : null;
  },

  async getByStatus(status: OrganizationStatus): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('status', status)
      .order('name');

    if (error) {
      console.error('Error fetching organizations by status:', error);
      throw new Error('Impossible de charger les organisations');
    }

    return (data || []).map(toOrganization);
  },

  async search(query: string): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .or(`name.ilike.%${query}%,contact_name.ilike.%${query}%,contact_email.ilike.%${query}%`)
      .order('name');

    if (error) {
      console.error('Error searching organizations:', error);
      throw new Error('Erreur lors de la recherche');
    }

    return (data || []).map(toOrganization);
  },
};
